// Shell — landing page with the three role entry points + a tiny
// hash-based router so we ship without pulling in react-router for
// Phase 8. Route modules are code-split via dynamic import; the entry
// HTML and React runtime are shared across all four routes.
import { lazy, Suspense, useEffect, useState } from 'react';
import { ClearLocalStorageButton } from './ClearLocalStorageButton';
import { Landing } from './Landing';

type Route = 'home' | 'observer' | 'participant' | 'design';

const ObserverApp = lazy(() => import('@usability-suite/observer-app'));
const ParticipantApp = lazy(() => import('@usability-suite/participant-app'));
const DesignApp = lazy(() => import('@usability-suite/design-app'));

export function parseRoute(hash: string): Route {
  // The shell only owns the first path segment; sub-routes
  // (`#/design/<id>`, `#/observer/...`, etc.) belong to the
  // mounted app. Match by leading segment so we don't unmount
  // a sub-app the moment it navigates.
  const path = hash.replace(/^#/, '').replace(/^\//, '').toLowerCase();
  const head = path.split('/', 1)[0] ?? '';
  if (head === 'observer') return 'observer';
  if (head === 'participant') return 'participant';
  if (head === 'design') return 'design';
  return 'home';
}

export function App() {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));

  useEffect(() => {
    const onChange = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  return (
    <>
      <Suspense
        fallback={
          <main style={{ padding: 'var(--pad)' }}>
            <p>Loading…</p>
          </main>
        }
      >
        {route === 'home' ? <Landing /> : null}
        {route === 'observer' ? <ObserverApp /> : null}
        {route === 'participant' ? <ParticipantApp /> : null}
        {route === 'design' ? <DesignApp /> : null}
      </Suspense>
      <ClearLocalStorageButton />
    </>
  );
}
